
/* 
  Developed by: Jordyn Kao
  Contact: jordyninternational@gmail.com
  This component is part of the application and contains internal credits.
  This code is protected under the terms of the contract agreement.
  Unauthorized use or distribution of this code is prohibited.
*/
import { motion } from 'framer-motion';
import { LinkButton } from '@/components/Button';
import { useEffect, useState } from 'react';

const StrategySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate progress from 0 to 252 (70% of 360)
          const duration = 2000; // 2 seconds
          const start = Date.now();
          
          const animate = () => {
            const now = Date.now();
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            
            setProgress(progress * 252);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );
    
    const element = document.querySelector('#strategy-section');
    if (element) {
      observer.observe(element);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <section id="strategy-section" className="py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <div className="space-y-6">
            <motion.h2 
              initial={{ opacity: 0, x: -20 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl font-bold text-gray-900 leading-tight"
            >
              The Modern Portfolio,<br />
              <span className="text-[hsl(var(--bronze-gold))]">Reimagined</span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-gray-600 leading-relaxed"
            >
              Our strategy is built on the principle that true diversification involves more than just stocks and bonds. By intelligently blending private market assets with traditional investments, we help you build a more resilient portfolio designed for superior, long-term growth.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <LinkButton 
                to="/why-altax" 
                variant="solid"
                className="group rounded-full px-8 py-4 font-semibold text-base transition-transform duration-300 transform hover:scale-105"
              >
                <span className="flex items-center space-x-2">
                  <span>Learn more about ALTA-X</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              </LinkButton>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="relative"
          >
            <div className="w-full max-w-md mx-auto relative">
              {/* Animated circle */}
              <div className="relative w-full aspect-square">
                {/* Outer glow */}
                <motion.div 
                  className="absolute inset-0 rounded-full bg-[hsl(var(--bronze-gold))]/20 blur-xl"
                  animate={isVisible ? { scale: 1.1 } : { scale: 1 }}
                  transition={{ 
                    repeat: Infinity, 
                    repeatType: 'reverse',
                    duration: 2,
                    ease: 'easeInOut'
                  }}
                />
                
                {/* Main circle */}
                <div className="w-full h-full rounded-full border-8 border-gray-100 relative overflow-hidden">
                  <motion.div 
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `conic-gradient(
                        hsl(var(--bronze-gold)) 0deg ${progress}deg, 
                        #e5e7eb ${progress}deg 360deg
                      )`
                    }}
                  />
                  
                  {/* Inner circle */}
                  <div className="absolute inset-8 bg-white rounded-full flex items-center justify-center">
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={isVisible ? { scale: 1, opacity: 1 } : {}}
                      transition={{ delay: 1, duration: 0.5 }}
                      className="text-center"
                    >
                      <div className="text-3xl font-bold text-gray-900 mb-1">Diversified</div>
                      <div className="text-lg text-gray-600">Growth</div>
                      <div className="text-sm text-gray-500 mt-2">Private Market Access</div>
                    </motion.div>
                  </div>
                </div>
              </div>
              
              {/* Legend */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1.2, duration: 0.5 }}
                className="mt-8 grid grid-cols-2 gap-4 text-sm"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-[hsl(var(--bronze-gold))]"></div>
                  <span>Real Estate (70%)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-gray-200"></div>
                  <span>Other Assets (30%)</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default StrategySection;
